(() => {
  'use strict';
  if (window.CACBUpsells) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const collectionUrl = (handle) => `${root()}collections/${handle}`;
  const handles = {
    balm: 'cast-iron-conditioning-balm',
    tools: 'handmade-kitchen-utensils-and-accessories',
    trivets: 'cast-iron-dutch-ovens-trivets-kettles',
    turner: 'large-handmade-kitchen-turner',
    rolling: 'handspun-french-rolling-pin-light-finish',
    chopsticks: 'handspun-chopsticks',
    scraper: 'pot-pan-scraper'
  };
  let catalog;
  let cart = { items: [] };
  let cartRequest;
  let busy = false;
  let queued = false;
  let sequence = 0;
  const selected = new WeakMap();
  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || catalog.currency || 'USD' }).format(Number(cents) / 100);
  const category = (type, title) => {
    const text = `${type || ''} ${title || ''}`.toLowerCase();
    if (/\btrivet\b|\blid only\b|\breplacement lid\b|\b(?:skillet|oven|pot)\s+(?:lid|cover)\b|\b(?:lid|cover)\s+for\b/.test(String(title || '').toLowerCase()) || /^(lids?|accessories|merchandise|culinary accessories)$/i.test(type || '')) return null;
    if (/waffle/.test(text)) return 'waffle';
    if (/griddle/.test(text)) return 'griddle';
    if (/dutch|kettle|roaster/.test(text)) return 'dutch';
    if (/bakeware|muffin|cornbread|gem pan|bread pan|baking/.test(text)) return 'bakeware';
    if (/skillet|frying pan|chicken fryer/.test(text)) return 'skillet';
    return null;
  };
  const inCart = (id) => cart.items.some((line) => String(line.variant_id || line.id) === String(id));
  const choices = (products, excludeId) => products.filter((product) => String(product.id) !== String(excludeId)).flatMap((product) => (product.variants || []).map((variant) => ({ product, variant })));
  const lead = (products, handle) => products.slice().sort((a, b) => Number(b.handle === handle) - Number(a.handle === handle));
  const byAvailablePrice = (products) => {
    const price = (product) => Math.min(...(product.variants || []).filter((variant) => variant.available).map((variant) => Number(variant.price)));
    return products.slice().sort((a, b) => price(a) - price(b));
  };
  const group = (key, title, label, products, collection) => ({ key, title, label, products, url: collectionUrl(collection) });
  function groupsFor(kind) {
    const tools = catalog.tools || [];
    const utensils = tools.filter((product) => !/scraper/i.test(product.title));
    const rolling = tools.filter((product) => /rolling pin/i.test(product.title));
    const balm = group('balm', 'Seasoning balm', 'Choose size', (catalog.balm || []).filter((p) => p.handle !== 'cast-iron-conditioning-balm-2-oz'), handles.balm);
    const scraper = group('scraper', 'Pot & pan scraper', 'Choose option', tools.filter((p) => p.handle === handles.scraper), handles.tools);
    const utensilsGroup = (first) => group('utensils', 'Handmade Utensils', 'Choose utensil', lead(utensils, first), handles.tools);
    if (kind === 'dutch') return [balm, utensilsGroup(handles.rolling), group('trivets', 'Trivets', 'Choose table trivet', byAvailablePrice(catalog.trivets || []), handles.trivets)];
    if (kind === 'bakeware') return [balm, group('rolling', 'French rolling pins', 'Choose wood', lead(rolling, handles.rolling), handles.tools), scraper];
    if (kind === 'griddle') return [balm, utensilsGroup(handles.chopsticks), scraper];
    if (kind === 'waffle') return [balm, utensilsGroup(handles.turner), scraper];
    return [balm, scraper, utensilsGroup(handles.turner)];
  }
  function optionName(choice, key) {
    const { product, variant } = choice;
    let title = key === 'balm' ? product.title.replace(/^Cast Iron Conditioning Balm\s*\(?/i, '').replace(/\)$/, '') : product.title;
    if (variant.title && variant.title !== 'Default Title' && key !== 'balm' && product.variants.length > 1) title += ` · ${variant.title}`;
    return `${title} · ${money(variant.price)}${!variant.available ? ' · Sold out' : inCart(variant.id) ? ' · In cart' : ''}`;
  }
  function cardHTML(item, host, index) {
    const list = choices(item.products, host.dataset.productId);
    const previous = selected.get(host)?.[item.key];
    const available = list.filter((choice) => choice.variant.available && !inCart(choice.variant.id));
    const requiresChoice = item.key === 'trivets';
    const active = available.find((choice) => String(choice.variant.id) === String(previous)) || (!requiresChoice ? available[0] : null);
    const example = requiresChoice ? list.find((choice) => choice.variant.available && choice.product.handle === 'griswold-made-flat-iron-trivet-large-table-trivet-1725-perfect-for-your-kitchen-or-dinner-table') : null;
    const shown = active || example || list.find((choice) => choice.variant.available) || list[0];
    const uid = `cacb-choice-${host.dataset.cacbUid}-${index}`;
    const image = shown?.variant.image || shown?.product.image;
    const href = shown && (!requiresChoice || active) ? `${shown.product.url}?variant=${shown.variant.id}` : item.url;
    const unavailableText = list.length && list.every((choice) => inCart(choice.variant.id) || !choice.variant.available) && list.some((choice) => inCart(choice.variant.id)) ? 'In your cart' : 'Unavailable';
    return `<article class="cacb-addon-card" data-group="${item.key}">
      <a class="cacb-addon-card__image" href="${esc(href)}" aria-label="${esc(requiresChoice && !active ? 'Browse table-trivet styles and dimensions' : shown?.product.title || item.title)}">${image ? `<img src="${esc(image)}" alt="${esc(requiresChoice && !active ? `Example table trivet: ${shown.product.title}` : shown.product.title)}" width="480" height="480" loading="lazy">` : '<span>View collection</span>'}</a>
      <div class="cacb-addon-card__body"><h3>${esc(item.title)}</h3>
        <p class="cacb-addon-card__price">${requiresChoice && !active && available.length ? 'Choose a style for price' : shown ? money(shown.variant.price) : 'Currently unavailable'}</p>
        <label for="${uid}">${esc(item.label)}</label>
        <select id="${uid}" data-cacb-choice ${!available.length ? 'disabled' : ''}>${requiresChoice ? `<option value="" ${!active ? 'selected' : ''} disabled>Choose table trivet</option>` : ''}${list.length ? list.map((choice) => `<option value="${choice.variant.id}" ${String(choice.variant.id) === String(active?.variant.id) ? 'selected' : ''} ${!choice.variant.available || inCart(choice.variant.id) ? 'disabled' : ''}>${esc(optionName(choice, item.key))}</option>`).join('') : '<option>No available options</option>'}</select>
        <button type="button" class="cacb-addon-card__add" data-cacb-add ${!active || busy ? 'disabled' : ''}>${active || requiresChoice && available.length ? 'Add' : unavailableText}</button>
        ${requiresChoice ? '<small>Check dimensions for your cookware.</small>' : ''}
        <a class="cacb-addon-card__browse" href="${esc(item.url)}">${item.key === 'balm' ? 'View all balm sizes' : item.key === 'trivets' ? 'Browse trivets & check size' : 'Browse handmade tools'}</a>
      </div></article>`;
  }
  function updateProductScroll(host) {
    if (host.dataset.placement !== 'product') return;
    const track = host.querySelector('.cacb-addon__track');
    const nav = host.querySelector('[data-cacb-scroll-nav]');
    if (!track || !nav) return;
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
    const position = Math.max(0, Math.min(maximum, window.getComputedStyle(track).direction === 'rtl' ? -track.scrollLeft : track.scrollLeft));
    nav.hidden = maximum <= 2;
    nav.querySelector('[data-cacb-scroll="previous"]').disabled = position <= 2;
    nav.querySelector('[data-cacb-scroll="next"]').disabled = position >= maximum - 2;
    nav.querySelector('[data-cacb-scroll-cue]').textContent = position >= maximum - 2 ? 'Swipe back for more' : 'Swipe for more';
  }
  function bindProductScroll(host) {
    if (host.dataset.placement !== 'product') return;
    const track = host.querySelector('.cacb-addon__track');
    const nav = host.querySelector('[data-cacb-scroll-nav]');
    if (!track || !nav) return;
    track.addEventListener('scroll', () => updateProductScroll(host), { passive: true });
    nav.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cacb-scroll]');
      if (!button || button.disabled) return;
      const style = window.getComputedStyle(track);
      const card = track.querySelector('.cacb-addon-card');
      const distance = (card?.getBoundingClientRect().width || track.clientWidth) + (Number.parseFloat(style.columnGap) || 0);
      const direction = (button.dataset.cacbScroll === 'next' ? 1 : -1) * (style.direction === 'rtl' ? -1 : 1);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: distance * direction, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    updateProductScroll(host);
  }
  function render(host) {
    if (host.dataset.cacbUncertain === 'true') return;
    let kind;
    if (host.dataset.placement === 'product') {
      kind = category(host.dataset.productType, host.dataset.productTitle);
      const earlier = Array.from(document.querySelectorAll('[data-cacb-upsell][data-placement="product"]')).find((item) => item.dataset.productId === host.dataset.productId);
      if (!kind || earlier !== host) { host.hidden = true; return; }
    } else {
      if (!cart.items.length) { host.hidden = true; return; }
      const source = cart.items.find((line) => category(line.product_type, line.product_title || line.title));
      kind = source ? category(source.product_type, source.product_title || source.title) : 'skillet';
    }
    const fingerprint = `${kind}|${cart.items.map((item) => `${item.variant_id || item.id}:${item.quantity}`).join(',')}`;
    if (host.dataset.cacbFingerprint === fingerprint) return;
    host.dataset.cacbFingerprint = fingerprint;
    host.dataset.cacbUid ||= String(++sequence);
    const title = host.dataset.placement === 'product' ? 'Popular with this item' : "Don't forget to add...";
    const scroll = host.querySelector('.cacb-addon__track')?.scrollLeft || 0;
    const headingId = `cacb-heading-${host.dataset.cacbUid}`;
    const trackId = `cacb-track-${host.dataset.cacbUid}`;
    const scrollNav = host.dataset.placement === 'product' ? `<div class="cacb-addon__scroll-nav" data-cacb-scroll-nav hidden><p data-cacb-scroll-cue>Swipe for more</p><div class="cacb-addon__scroll-buttons"><button type="button" data-cacb-scroll="previous" aria-label="Show previous accessory" aria-controls="${trackId}" disabled><span aria-hidden="true">←</span></button><button type="button" data-cacb-scroll="next" aria-label="Show next accessory" aria-controls="${trackId}"><span aria-hidden="true">→</span></button></div></div>` : '';
    host.innerHTML = `<section class="cacb-addon cacb-addon--${host.dataset.placement}" aria-labelledby="${headingId}"><h2 id="${headingId}">${title}</h2>${scrollNav}<div id="${trackId}" class="cacb-addon__track" aria-label="Accessory choices">${groupsFor(kind).map((item, index) => cardHTML(item, host, index)).join('')}</div><p class="cacb-addon__status" role="status" aria-live="polite"></p></section>`;
    host.hidden = false;
    host.querySelector('.cacb-addon__track').scrollLeft = scroll;
    bindProductScroll(host);
  }
  function mount() {
    if (!catalog) {
      const source = document.getElementById('cacb-upsell-catalog');
      if (!source) return;
      try { catalog = JSON.parse(source.textContent); cart = catalog.cart || cart; } catch (_) { return; }
    }
    document.querySelectorAll('[data-cacb-upsell]').forEach(render);
  }
  function scheduleMount() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; mount(); });
  }
  function readCart() {
    if (cartRequest) return cartRequest;
    cartRequest = fetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error('Unable to check your cart.');
      return response.json();
    }).then((data) => { cart = data; return data; }).finally(() => { cartRequest = null; });
    return cartRequest;
  }
  function announce(message) {
    let live = document.getElementById('cacb-upsell-announcement');
    if (!live) {
      live = document.createElement('p');
      live.id = 'cacb-upsell-announcement';
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = message;
  }
  function sectionTargets() {
    const byId = new Map();
    document.querySelectorAll('page-header, cart-drawer, cart-form').forEach((element) => {
      const id = element.dataset.sectionId;
      const method = typeof element.updateFromCartChange === 'function' ? 'updateFromCartChange' : typeof element.refreshFromHtml === 'function' ? 'refreshFromHtml' : null;
      if (id && method && !byId.has(id)) byId.set(id, { element, method, id });
    });
    return Array.from(byId.values()).slice(0, 5);
  }
  function refreshSections(data, targets) {
    let complete = targets.length > 0;
    targets.forEach(({ element, method, id }) => {
      const html = data.sections?.[id];
      if (typeof html !== 'string' || !html.trim()) { complete = false; return; }
      try { element[method](html); } catch (_) { complete = false; }
    });
    if (!complete) document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
  }
  function findChoice(card) {
    const id = card.querySelector('[data-cacb-choice]')?.value;
    return choices([...(catalog.balm || []), ...(catalog.tools || []), ...(catalog.trivets || [])]).find((choice) => String(choice.variant.id) === String(id));
  }
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-cacb-choice]');
    if (!select) return;
    const card = select.closest('.cacb-addon-card');
    const host = select.closest('[data-cacb-upsell]');
    const choice = findChoice(card);
    if (!choice) return;
    selected.set(host, { ...selected.get(host), [card.dataset.group]: choice.variant.id });
    const imageLink = card.querySelector('.cacb-addon-card__image');
    imageLink.href = `${choice.product.url}?variant=${choice.variant.id}`;
    imageLink.setAttribute('aria-label', choice.product.title);
    const image = imageLink.querySelector('img');
    if (image) { image.src = choice.variant.image || choice.product.image; image.alt = choice.product.title; }
    card.querySelector('.cacb-addon-card__price').textContent = money(choice.variant.price);
    card.querySelector('[data-cacb-add]').disabled = busy || !choice.variant.available || inCart(choice.variant.id);
  });
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cacb-add]');
    if (!button || button.disabled || busy) return;
    const host = button.closest('[data-cacb-upsell]');
    const card = button.closest('.cacb-addon-card');
    const choice = findChoice(card);
    if (!choice?.variant.available) return;
    const status = host.querySelector('.cacb-addon__status');
    const placement = host.dataset.placement;
    let sent = false;
    let confirmed = false;
    let before = 0;
    busy = true;
    document.querySelectorAll('[data-cacb-add]').forEach((item) => { item.disabled = true; });
    button.textContent = 'Adding…';
    card.querySelector('select').disabled = true;
    status.textContent = '';
    try {
      await readCart();
      if (inCart(choice.variant.id)) {
        status.textContent = 'This option is already in your cart.';
        announce(status.textContent);
        return;
      }
      before = cart.items.filter((line) => String(line.variant_id || line.id) === String(choice.variant.id) && line.properties?._cacb_upsell === placement).reduce((sum, line) => sum + line.quantity, 0);
      const targets = sectionTargets();
      const body = { items: [{ id: choice.variant.id, quantity: 1, properties: { _cacb_upsell: placement } }], sections: targets.map((target) => target.id), sections_url: window.location.pathname };
      sent = true;
      const response = await fetch(`${root()}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await response.json();
      if (!response.ok || data.status) {
        sent = false;
        if (response.status === 422) choice.variant.available = false;
        throw new Error(typeof data.description === 'string' ? data.description : 'This option could not be added. Please choose another.');
      }
      confirmed = true;
      refreshSections(data, targets);
      status.textContent = `Added ${choice.product.title}.`;
      announce(status.textContent);
      if (typeof window.clarity === 'function') window.clarity('event', `cacb_upsell_add_${placement}`);
      document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: choice.variant.id, source: 'cacb-upsell', placement } }));
      if (placement === 'product' && window.theme?.settings?.afterAddToCart === 'drawer') {
        const drawer = document.querySelector('cart-drawer');
        if (typeof drawer?.open === 'function') drawer.open(button);
      }
      await readCart();
    } catch (error) {
      if (sent && !confirmed) {
        try {
          await readCart();
          const after = cart.items.filter((line) => String(line.variant_id || line.id) === String(choice.variant.id) && line.properties?._cacb_upsell === placement).reduce((sum, line) => sum + line.quantity, 0);
          confirmed = after > before;
          status.textContent = confirmed ? `Added ${choice.product.title}.` : 'The add could not be confirmed. Check your cart before trying again.';
        } catch (_) { status.textContent = 'Connection interrupted. Check your cart before trying again.'; }
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      } else {
        status.textContent = confirmed ? 'Added to your cart. Open your cart to review it.' : error.message;
      }
      announce(status.textContent);
      status.classList.add('cacb-addon__status--notice');
      if (sent && !confirmed) {
        const link = document.createElement('a');
        link.href = `${root()}cart`;
        link.textContent = ' View cart';
        status.appendChild(link);
        host.dataset.cacbUncertain = 'true';
      }
    } finally {
      busy = false;
      document.querySelectorAll('[data-cacb-upsell]').forEach((item) => {
        if (item.dataset.cacbUncertain === 'true') return;
        const message = item === host ? status.textContent : '';
        delete item.dataset.cacbFingerprint;
        render(item);
        if (message && item.querySelector('.cacb-addon__status')) item.querySelector('.cacb-addon__status').textContent = message;
      });
      scheduleMount();
    }
  });
  const sync = () => readCart().then(scheduleMount).catch(() => {});
  ['on:cart:add', 'on:cart:change', 'on:cart:after-merge'].forEach((name) => document.addEventListener(name, sync));
  document.addEventListener('on:cart-drawer:before-open', sync);
  document.addEventListener('shopify:section:load', scheduleMount);
  window.addEventListener('resize', () => document.querySelectorAll('[data-cacb-upsell][data-placement="product"]').forEach(updateProductScroll), { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true }); else mount();
  new MutationObserver((records) => {
    if (records.some((record) => Array.from(record.addedNodes).some((node) => node.nodeType === 1 && (node.matches?.('[data-cacb-upsell]') || node.querySelector?.('[data-cacb-upsell]'))))) scheduleMount();
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.CACBUpsells = { mount, category, groupsFor };
})();
